import { useCallback, useEffect, useRef, useState, RefObject } from 'react';

const SEEK_STEP_IN_SECONDS = 5;
const DEFAULT_VOLUME = 50;

interface UseMediaPlayerParams {
  mediaRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  /** 씬 구간 재생 — 비디오 전용 (없으면 전체 재생) */
  sceneRange?: { startSeconds: number; endSeconds: number };
  /** 서버에서 받은 duration (초). 미디어 메타데이터 로드 전 fallback으로 사용 */
  fallbackDuration?: number;
}

interface UseMediaPlayerResult {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  seekStepInSeconds: number;
  handlePlayPause: () => void;
  handleTimeChange: (timeInSeconds: number) => void;
  goToPreviousSeconds: () => void;
  goToNextSeconds: () => void;
  changeVolume: (newVolume: number) => void;
  handleSoundToggle: () => void;
}

/**
 * HTML5 비디오/오디오 통합 제어 훅
 * - 재생/일시정지, 시간 제어, 볼륨 제어
 * - sceneRange가 있으면 구간 재생 (sceneStart ~ sceneEnd)
 * - sceneRange가 없으면 전체 재생
 * - fallbackDuration으로 메타데이터 로드 전 duration 표시
 *
 * Vercel Best Practices 적용:
 * - rerender-dependencies: useEffect deps에 원시값만 사용
 * - rerender-derived-state-no-effect: duration은 렌더링 중 계산
 * - rerender-functional-setstate: 함수형 setState
 * - rerender-use-ref-transient-values: volumeRef로 이전 볼륨 복원
 * - js-early-exit: 모든 핸들러에서 early return
 */
export function useMediaPlayer({
  mediaRef,
  sceneRange,
  fallbackDuration,
}: UseMediaPlayerParams): UseMediaPlayerResult {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [fullDuration, setFullDuration] = useState(fallbackDuration ?? 0);
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const [isMuted, setIsMuted] = useState(false);
  // rerender-use-ref-transient-values: 볼륨 복원용 ref
  const volumeRef = useRef(DEFAULT_VOLUME);

  // rerender-derived-state-no-effect: 렌더링 중 계산
  const startSeconds = sceneRange?.startSeconds ?? 0;
  const endSeconds = sceneRange?.endSeconds ?? fullDuration;
  const duration = sceneRange
    ? sceneRange.endSeconds - sceneRange.startSeconds
    : fullDuration;

  // 미디어 초기화: 시작 시간 및 볼륨 설정
  // rerender-dependencies: 원시값만 의존성에 사용
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const handleLoadedMetadata = () => {
      const mediaDuration = media.duration;
      if (Number.isFinite(mediaDuration) && mediaDuration > 0) {
        setFullDuration(mediaDuration);
      }
      media.currentTime = startSeconds;
      setCurrentTime(0);
    };

    media.addEventListener('loadedmetadata', handleLoadedMetadata);

    // 이미 로드된 경우
    if (media.readyState >= 1) {
      const mediaDuration = media.duration;
      if (Number.isFinite(mediaDuration) && mediaDuration > 0) {
        setFullDuration(mediaDuration);
      }
      media.currentTime = startSeconds;
      setCurrentTime(0);
    }

    // 볼륨 초기화
    media.volume = DEFAULT_VOLUME / 100;

    return () => {
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [mediaRef, startSeconds]);

  // 시간 업데이트 및 구간 끝 감지
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const handleTimeUpdate = () => {
      const relativeTime = media.currentTime - startSeconds;
      setCurrentTime(Math.max(0, Math.min(relativeTime, duration)));

      // 구간 끝에 도달하면 정지 (endSeconds가 0이면 아직 duration 미로드)
      if (endSeconds > 0 && media.currentTime >= endSeconds) {
        media.pause();
        media.currentTime = startSeconds;
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      media.currentTime = startSeconds;
      setIsPlaying(false);
      setCurrentTime(0);
    };

    // duration 변경 이벤트 (오디오에서 fallback → 실제 duration 교체)
    const handleDurationChange = () => {
      const mediaDuration = media.duration;
      if (Number.isFinite(mediaDuration) && mediaDuration > 0) {
        setFullDuration(mediaDuration);
      }
    };

    media.addEventListener('timeupdate', handleTimeUpdate);
    media.addEventListener('play', handlePlay);
    media.addEventListener('pause', handlePause);
    media.addEventListener('ended', handleEnded);
    media.addEventListener('durationchange', handleDurationChange);

    return () => {
      media.removeEventListener('timeupdate', handleTimeUpdate);
      media.removeEventListener('play', handlePlay);
      media.removeEventListener('pause', handlePause);
      media.removeEventListener('ended', handleEnded);
      media.removeEventListener('durationchange', handleDurationChange);
    };
  }, [mediaRef, startSeconds, endSeconds, duration]);

  // volume 값을 ref에 동기화
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  // js-early-exit: 모든 핸들러에서 early return
  const handlePlayPause = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;

    if (media.paused) {
      media.play().catch((error) => {
        console.warn('재생 시작 중 오류 발생:', error);
      });
    } else {
      media.pause();
    }
  }, [mediaRef]);

  const handleTimeChange = useCallback(
    (timeInSeconds: number) => {
      const media = mediaRef.current;
      if (!media) return;

      const clampedTime = Math.max(0, Math.min(timeInSeconds, duration));
      media.currentTime = startSeconds + clampedTime;
      setCurrentTime(clampedTime);
    },
    [mediaRef, startSeconds, duration],
  );

  const goToPreviousSeconds = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;

    const newTime = Math.max(
      startSeconds,
      media.currentTime - SEEK_STEP_IN_SECONDS,
    );
    media.currentTime = newTime;
  }, [mediaRef, startSeconds]);

  const goToNextSeconds = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;

    const newTime = Math.min(
      endSeconds,
      media.currentTime + SEEK_STEP_IN_SECONDS,
    );
    media.currentTime = newTime;
  }, [mediaRef, endSeconds]);

  const changeVolume = useCallback(
    (newVolume: number) => {
      const media = mediaRef.current;
      if (!media) return;

      const clampedVolume = Math.min(100, Math.max(0, newVolume));
      media.volume = clampedVolume / 100;
      setVolume(clampedVolume);
      volumeRef.current = clampedVolume;

      if (clampedVolume > 0 && media.muted) {
        media.muted = false;
        setIsMuted(false);
      }
      if (clampedVolume === 0) {
        setIsMuted(true);
      }
    },
    [mediaRef],
  );

  const handleSoundToggle = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;

    const currentVolume = volumeRef.current;
    if (media.muted || currentVolume === 0) {
      media.muted = false;
      setIsMuted(false);
      if (currentVolume === 0) {
        const restoreVolume = DEFAULT_VOLUME;
        media.volume = restoreVolume / 100;
        setVolume(restoreVolume);
        volumeRef.current = restoreVolume;
      }
    } else {
      media.muted = true;
      setIsMuted(true);
    }
  }, [mediaRef]);

  return {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    seekStepInSeconds: SEEK_STEP_IN_SECONDS,
    handlePlayPause,
    handleTimeChange,
    goToPreviousSeconds,
    goToNextSeconds,
    changeVolume,
    handleSoundToggle,
  };
}
